import React, { useCallback, useId } from 'react';
import type { CustomField } from '@puckeditor/core';
import styled from 'styled-components';

import { FieldLabelWithHelp } from './field-label-with-help';
import { fieldControlStyle } from './field-control-style';

/**
 * Nonblocking character-count guidance thresholds (ECOMM-14849): typical display lengths for a
 * search result's title/description, not hard validation limits -- a longer value still saves,
 * publishes, and renders. The count simply turns amber past the threshold.
 */
export const SEO_TITLE_GUIDANCE_LIMIT = 60;
export const SEO_DESCRIPTION_GUIDANCE_LIMIT = 160;

type SeoTextFieldProps = {
  field: { label?: string };
  name: string;
  // Declared `string` by Puck's CustomField contract, but a custom field's runtime value isn't
  // actually constrained by that type -- saved/unsaved root props can arrive null/undefined.
  // Widened here (rather than suppressing the `??` below) so the guard stays honest about what
  // the runtime can hand us.
  value: string | null | undefined;
  onChange: (value: string) => void;
  readOnly?: boolean;
};

type SeoTextFieldOptions = {
  limit: number;
  multiline?: boolean;
  hint?: string;
  placeholder?: string;
};

const CountHint = styled.span<{ $over: boolean }>`
  display: block;
  margin-top: 4px;
  font-size: 11.5px;
  color: ${({ $over }) =>
    $over ? 'var(--dcm-cms-seo-count-over-color, #b26a00)' : 'var(--dcm-cms-seo-count-color, #767676)'};
`;

function seoTextFieldWidget({ limit, multiline, hint, placeholder }: SeoTextFieldOptions) {
  return function SeoTextFieldWidget({ field, name, value, onChange, readOnly }: SeoTextFieldProps): JSX.Element {
    // Gotcha: a custom field's runtime value isn't constrained by its declared `string` type --
    // saved data can arrive null/undefined even though TypeScript says otherwise.
    const text = value ?? '';
    const over = text.length > limit;
    const countId = useId();

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange(e.currentTarget.value),
      [onChange]
    );

    const controlStyle = { ...fieldControlStyle, resize: multiline ? ('vertical' as const) : undefined };

    return (
      <FieldLabelWithHelp label={field.label ?? name} help={hint} readOnly={readOnly}>
        {multiline ? (
          <textarea
            rows={3}
            value={text}
            onChange={handleChange}
            disabled={readOnly}
            placeholder={placeholder}
            aria-label={field.label ?? name}
            aria-describedby={countId}
            data-testid={`seo-text-${name}`}
            style={controlStyle}
          />
        ) : (
          <input
            type='text'
            value={text}
            onChange={handleChange}
            disabled={readOnly}
            placeholder={placeholder}
            aria-label={field.label ?? name}
            aria-describedby={countId}
            data-testid={`seo-text-${name}`}
            style={controlStyle}
          />
        )}
        <CountHint id={countId} $over={over}>
          {text.length}/{limit} characters{over ? ' — longer than typically shown in full' : ''}
        </CountHint>
      </FieldLabelWithHelp>
    );
  };
}

/**
 * A single/multi-line text field with nonblocking character-count guidance (ECOMM-14849).
 * The count is informational only -- it never prevents typing, saving, or publishing a longer
 * value, it just turns amber past `limit` so an author can judge how a search engine is likely
 * to truncate it.
 */
export function seoTextField(
  label: string,
  options: SeoTextFieldOptions & { aiInstructions?: string }
): CustomField<string> {
  const { aiInstructions, ...widgetOptions } = options;
  return {
    type: 'custom',
    label,
    render: seoTextFieldWidget(widgetOptions),
    ai: {
      schema: { type: 'string' },
      ...(aiInstructions ? { instructions: aiInstructions } : {}),
    },
  };
}
